import { logger } from '@accedo/logger';
import { deviceApi, DeviceModuleApi } from '@accedo/ctv-device-api';
import { config } from '##/services/store/store';
import { sendApiRequest } from '##/services/tenApi/apirequest';
import { getHbbTvPlatformVersion } from '##/utils';

const UNKNOWN = 'Unknown';

type DeviceInfoKey = keyof DeviceModuleApi;

const {
  api: { apiBaseUrl },
} = config.$get();

export type IpBlockType = {
  ip: string;
  state: string;
};

export interface DeviceInformation {
  model: string;
  deviceId: string;
  platform: string;
  ipAddress: IpBlockType;
  firmwareVersion: string;
  manufacturer: string;
}

export const getIpAddress = async (): Promise<IpBlockType> => {
  const ipAddressEndpoint = `${apiBaseUrl}/whatismyip`;
  let ipAddress;
  try {
    ipAddress = await sendApiRequest<IpBlockType>(ipAddressEndpoint);
  } catch (err) {
    logger.error(err);
  }
  return ipAddress || { ip: '', state: '' };
};

const getDeviceInfoProperty = (key: DeviceInfoKey) => async (): Promise<
  string | typeof UNKNOWN
> => {
  let deviceInfoProperty;

  try {
    const prop = deviceApi.device?.[key];
    if (typeof prop === 'function') {
      deviceInfoProperty = ((await (prop as () => Promise<unknown>)())) || '';
    }
  } catch (err) {}

  return (deviceInfoProperty as string) || UNKNOWN;
};

const findManufacturer = (): string => {
  /* In case deviceApi.device.operator() returns nothing, which is observed on older devices */
  const manufacturer = navigator.userAgent
    .toLowerCase()
    ?.match(/hisense|tcl|sony|webos|samsung|panasonic|roku/g)?.[0];

  if (manufacturer === 'webos') {
    return 'LG';
  }

  return manufacturer?.toUpperCase?.() || UNKNOWN;
};

const getFirmwareVersion = getDeviceInfoProperty('firmwareVersion');
const getDeviceId = getDeviceInfoProperty('id');
const getDeviceModel = getDeviceInfoProperty('model');
const getDeviceOperator = getDeviceInfoProperty('operator');

export const getDeviceInformation = async (): Promise<DeviceInformation> => {
  const [
    deviceId,
    firmwareVersion,
    ipAddress,
    model,
    operator,
  ] = await Promise.all([
    getDeviceId(),
    getFirmwareVersion(),
    getIpAddress(),
    getDeviceModel(),
    getDeviceOperator(),
  ]);

  const manufacturer = operator === UNKNOWN ? findManufacturer() : operator;

  const platform = getHbbTvPlatformVersion();

  return {
    deviceId,
    firmwareVersion,
    ipAddress,
    model,
    platform,
    manufacturer,
  };
};

export const isDeviceWorkstation = (): boolean =>
  process.env.APP_PLATFORM === 'workstation';

export const isDeviceIQ = (): boolean =>
  ['iq', 'foxtel'].includes(process.env.APP_PLATFORM as string);

export const isDeviceMagneto = (): boolean =>
  process.env.APP_PLATFORM === 'firebolt';

export const isDeviceIQ3 = async (): Promise<boolean> => {
  const devicePlatform = await deviceApi.device.platform?.();

  return devicePlatform?.toLowerCase() === 'iq3';
};
